/**
 * 1. player.y can only be between bottom of screen and y-max
 * 2. player.x can be anywhere along the horizontal
 * 3. if player.y below bottom of screen, he dies
 * 4. if player.y at y-max, platforms should move. all other times, platforms stay still.
 */

import { GAME_HEIGHT, GAME_WIDTH, PLAYER_CUTOFF } from "./constants";
import * as PIXI from "pixi.js";
import { Keyboard } from "./keyboard";
import { Mouse } from "./mouse";
import { Player } from "./player";
import { Platform, PlatformManager, Enemy } from "./platformManager";
import { Bullet } from "./bullet";
import { GameManager } from "./gameManager";
import { SoundManager } from "./soundManager";

export class Game {
	private static instance: Game;

	public container: PIXI.Container;
	public ticker: PIXI.Ticker;
	public mouse: PIXI.Point;
	public bullets: Bullet[];
	public firstBullet: boolean; // TODO: Need a better solution!
	public platformManager: PlatformManager;
	public player: Player;
	public isGameOver: boolean;
	public deadPlayerFalling: boolean;
	public score: number;
	public scoreText: PIXI.Text;
	public writtenScore: boolean;
	public gameOverText: PIXI.Text;
	public playAgainText: PIXI.Text;
	public mainMenuText: PIXI.Text;

	private constructor(
		container: PIXI.Container,
		ticker: PIXI.Ticker,
		mouse: PIXI.Point
	) {
		this.container = container;
		this.ticker = ticker;
		this.ticker.add((delta) => this.update(delta));
		this.mouse = mouse;

		this.isGameOver = false;
		this.deadPlayerFalling = false;

		this.platformManager = new PlatformManager();
		this.container.addChild(this.platformManager.container);

		this.player = new Player(mouse);
		this.container.addChild(this.player.sprite);
		this.container.addChild(this.player.gunSprite);
		this.container.addChild(this.player.graphics);

		this.bullets = [];
		this.firstBullet = false;

		this.score = 0;
		this.scoreText = new PIXI.Text(
			this.score.toString(),
			new PIXI.TextStyle({
				fontFamily: "MrBubbleFont",
				fontSize: 72,
				fontWeight: "bold",
			})
		);
		this.scoreText.anchor.set(0.5);
		this.scoreText.position.set(GAME_WIDTH / 2, 100);
		this.container.addChild(this.scoreText);

		this.gameOverText = new PIXI.Text(
			"GAME OVER\nScore: ",
			new PIXI.TextStyle({
				fontFamily: "MrBubbleFont",
				fontSize: 72,
				fontWeight: "bold",
				align: "center",
			})
		);
		this.gameOverText.alpha = 0;
		this.gameOverText.anchor.set(0.5);
		this.gameOverText.position.set(GAME_WIDTH / 2, 400);
		this.container.addChild(this.gameOverText);

		this.playAgainText = new PIXI.Text(
			"Play Again",
			new PIXI.TextStyle({
				fontFamily: "MrBubbleFont",
				fontSize: 72,
				fontWeight: "bold",
				align: "center",
			})
		);
		this.playAgainText.alpha = 0;
		this.playAgainText.anchor.set(0.5);
		this.playAgainText.position.set(GAME_WIDTH / 2, 650);
		this.playAgainText.interactive = true;
		this.playAgainText.buttonMode = true;
		this.playAgainText.on("pointerover", () => {
			SoundManager.hover();
			this.playAgainText.style.fontSize = 92;
		});
		this.playAgainText.on("pointerout", () => {
			this.playAgainText.style.fontSize = 72;
		});
		this.playAgainText.on("pointerdown", function () {
			console.log("PLAY AGAIN");
			SoundManager.click();
			SoundManager.happy();
			GameManager.newGame();
		});
		this.container.addChild(this.playAgainText);

		this.mainMenuText = new PIXI.Text(
			"Main Menu",
			new PIXI.TextStyle({
				fontFamily: "MrBubbleFont",
				fontSize: 72,
				fontWeight: "bold",
				align: "center",
			})
		);
		this.mainMenuText.alpha = 0;
		this.mainMenuText.anchor.set(0.5);
		this.mainMenuText.position.set(GAME_WIDTH / 2, 782);
		this.mainMenuText.interactive = true;
		this.mainMenuText.buttonMode = true;
		this.mainMenuText.on("pointerover", () => {
			SoundManager.hover();
			this.mainMenuText.style.fontSize = 92;
		});
		this.mainMenuText.on("pointerout", () => {
			this.mainMenuText.style.fontSize = 72;
		});
		this.mainMenuText.on("pointerdown", function () {
			console.log("MAIN MENU");
			SoundManager.click();
			GameManager.mainMenu();
		});
		this.container.addChild(this.mainMenuText);

		this.writtenScore = false;
	}

	public static init(
		container: PIXI.Container,
		ticker: PIXI.Ticker,
		mouse: PIXI.Point
	): void {
		Game.instance = new Game(container, ticker, mouse);
	}

	update(delta: number): void {
		Keyboard.update();
		Mouse.update();

		if (!this.isGameOver && !this.deadPlayerFalling) {
			this.player.update(delta);

			let platformShouldMove = false;

			if (this.player.sprite.y <= PLAYER_CUTOFF) {
				platformShouldMove = true;
			}
			if (platformShouldMove) this.score++;
			this.platformManager.update(delta, platformShouldMove, this.player.vy);

			// Check collision from top (bouncing)
			this.platformManager.platforms.forEach((p) => {
				if (this.collision(this.player, p)) {
					if (p instanceof Enemy) {
						this.score += 1000;
					}
					this.player.vy = -20;
					SoundManager.jump();
				}
			});

			// Shooting
			if (Mouse.leftPressed()) {
				if (this.firstBullet) {
					const bullet = new Bullet(
						this.player.gunSprite.x,
						this.player.gunSprite.y,
						this.mouse.x,
						this.mouse.y
					);
					this.bullets.push(bullet);
					this.container.addChild(bullet.sprite);
					this.container.addChild(bullet.graphics);
					SoundManager.shoot();
				}
				this.firstBullet = true;
			}

			this.bullets.forEach((b) => {
				b.update();

				// Check for enemy collision
				this.platformManager.enemies.forEach((e) => {
					if (this.bulletCollision(b, e)) {
						this.score += 500;

						this.container.removeChild(b.sprite);
						this.container.removeChild(b.graphics);
						const bulletIndex = this.bullets.indexOf(b);
						if (bulletIndex > -1) {
							this.bullets.splice(bulletIndex, 1);
						}

						this.platformManager.container.removeChild(e.sprite);
						this.platformManager.container.removeChild(e.graphics);
						const enemyIndex = this.platformManager.enemies.indexOf(e);
						if (enemyIndex > -1) {
							this.platformManager.enemies.splice(enemyIndex, 1);
						}
						const moIndex = this.platformManager.platforms.indexOf(e);
						if (moIndex > -1) {
							this.platformManager.platforms.splice(moIndex, 1);
						}

						SoundManager.hit();
					}
				});

				if (
					b.sprite.x > GAME_WIDTH * 2 ||
					b.sprite.x < -GAME_WIDTH ||
					b.sprite.y > GAME_HEIGHT * 2 ||
					b.sprite.y < -GAME_HEIGHT
				) {
					const index = this.bullets.indexOf(b);

					if (index > -1) {
						this.bullets.splice(index, 1);
					}
				}
			});

			// Check player collision with enemy from bottom or side
			this.platformManager.enemies.forEach((e) => {
				if (this.enemyCollision(this.player, e)) {
					this.player.vy = 0;
					this.platformManager.enemiesShouldStop = true;
					this.deadPlayerFalling = true;
					SoundManager.die();
				}
			});
		} else if (this.isGameOver) {
			this.bullets.forEach((b) => b.update());
			if (this.scoreText.alpha != 0) {
				this.scoreText.alpha -= 0.05;
			}
			this.player.updateDeath(
				delta,
				this.platformManager.platforms[0].sprite.y < 0
			);
			this.platformManager.updateDeath(delta);

			if (this.player.sprite.y >= GAME_HEIGHT * 2) {
				this.player.sprite.y = GAME_HEIGHT * 2;
				if (!this.writtenScore) {
					this.gameOverText.text += this.score.toString();
				}
				this.writtenScore = true;
				this.showGameOver();
			}
		} else if (this.deadPlayerFalling) {
			this.bullets.forEach((b) => b.update());
			this.player.update(delta);
		}

		if (this.player.sprite.y >= GAME_HEIGHT && !this.isGameOver) {
			SoundManager.fall();
			SoundManager.fadeOutHappy();
			this.isGameOver = true;
			this.platformManager.platforms.forEach((p) => {
				p.deathVelocityY = -this.player.vy;
			});
			this.player.vy = -1;
		}

		// Update score
		this.scoreText.text = this.score.toString();
	}

	showGameOver(): void {
		// TODO: Wrap in GUI Container
		if (this.gameOverText.alpha != 1) {
			this.gameOverText.alpha += 0.05;
			this.mainMenuText.alpha += 0.05;
			this.playAgainText.alpha += 0.05;
		}
	}

	collision(player: Player, platform: Platform): boolean {
		if (player.vy > 0) {
			const playerBoundingBoxTop = player.collisionBox.y;
			const playerBoundingBoxBottom =
				player.collisionBox.y + player.collisionBox.height;
			const playerBoundingBoxLeft = player.collisionBox.x;
			const playerBoundingBoxRight =
				player.collisionBox.x + player.collisionBox.width;
			const platformBoundingBoxTop = platform.sprite.getBounds().y;
			const platformBoundingBoxBottom =
				platform.sprite.getBounds().y + platform.sprite.getBounds().height;
			const platformBoundingBoxLeft = platform.sprite.getBounds().x;
			const platformBoundingBoxRight =
				platform.sprite.getBounds().x + platform.sprite.getBounds().width;

			return (
				playerBoundingBoxTop <=
					platformBoundingBoxTop - player.collisionBox.height + 12 &&
				playerBoundingBoxBottom >= platformBoundingBoxTop &&
				playerBoundingBoxLeft < platformBoundingBoxRight &&
				playerBoundingBoxRight > platformBoundingBoxLeft
			);
		}

		return false;
	}

	enemyCollision(player: Player, enemy: Enemy): boolean {
		const playerBoundingBoxTop = player.sprite.y;
		const playerBoundingBoxBottom = player.sprite.y + player.sprite.height;
		const playerBoundingBoxLeft = player.sprite.x;
		const playerBoundingBoxRight = player.sprite.x + player.sprite.width;

		const enemyBoundingBoxTop = enemy.sprite.y;
		const enemyBoundingBoxBottom = enemy.sprite.y + enemy.sprite.height;
		const enemyBoundingBoxLeft = enemy.sprite.x;
		const enemyBoundingBoxRight = enemy.sprite.x + enemy.sprite.width;

		if (player.vy < 0) {
			// If player moving up and they collide in any way, its a collision
			return (
				playerBoundingBoxLeft < enemyBoundingBoxRight &&
				enemyBoundingBoxLeft < playerBoundingBoxRight &&
				playerBoundingBoxTop < enemyBoundingBoxBottom &&
				enemyBoundingBoxTop < playerBoundingBoxBottom
			);
		} else {
			return false;
		}
	}

	bulletCollision(bullet: Bullet, enemy: Enemy): boolean {
		const bulletBoundingBoxTop = bullet.sprite.y;
		const bulletBoundingBoxBottom = bullet.sprite.y + bullet.sprite.height;
		const bulletBoundingBoxLeft = bullet.sprite.x;
		const bulletBoundingBoxRight = bullet.sprite.x + bullet.sprite.width;

		const enemyBoundingBoxTop = enemy.sprite.y;
		const enemyBoundingBoxBottom = enemy.sprite.y + enemy.sprite.height;
		const enemyBoundingBoxLeft = enemy.sprite.x;
		const enemyBoundingBoxRight = enemy.sprite.x + enemy.sprite.width;

		return (
			bulletBoundingBoxLeft < enemyBoundingBoxRight &&
			enemyBoundingBoxLeft < bulletBoundingBoxRight &&
			bulletBoundingBoxTop < enemyBoundingBoxBottom &&
			enemyBoundingBoxTop < bulletBoundingBoxBottom
		);
	}
}
